import { useState, useEffect, useCallback } from 'react';
import { EditOutlined, FormOutlined } from '@ant-design/icons';
import { AModal, ASwitch, AInput } from '../../../components';
import { Form } from 'antd';
import { useToast } from '../../../hooks/toast';
import {
  schemaCovenantCreate,
  schemaCovenantEdit,
} from '../schemas/yup.covenant.schema';
import { ICovenant } from '../../../shared/interfaces';
import { patchCovenant, postCovenant } from '../../../http';
import { useI18n } from '../../../shared/i18n';

interface ICovenantProps {
  open: boolean;
  close: () => void;
  covenant: ICovenant | null;
  setCovenants: React.Dispatch<React.SetStateAction<ICovenant[]>>;
}

const CovenantCreateEdit: React.FC<ICovenantProps> = ({
  open,
  close,
  covenant,
  setCovenants,
}) => {
  const { addToast } = useToast();
  const { t } = useI18n();
  const [description, setDescription] = useState<string>('');
  const [status, setStatus] = useState<boolean>(false);

  const handleCreate = useCallback(async () => {
    schemaCovenantCreate
      .validate({
        description,
      })
      .then(async (validated) => {
        await postCovenant(validated)
          .then((data) => {
            setCovenants((state) => [...state, data]);
            addToast({
              type: 'success',
              title: t('covenantsModal.toast.successTitle'),
              description: t('covenantsModal.toast.createdDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('covenantsModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('covenantsModal.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('covenantsModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [description, status, t, addToast, close, setCovenants]);

  const handleEdit = useCallback(async () => {
    schemaCovenantEdit
      .validate({
        description,
        status,
      })
      .then(async (validated) => {
        await patchCovenant(covenant?.id, validated)
          .then((data) => {
            setCovenants((state) =>
              state.map((e) => (e.id !== data.id ? e : data)),
            );
            addToast({
              type: 'info',
              title: t('covenantsModal.toast.infoTitle'),
              description: t('covenantsModal.toast.updatedDescription'),
            });
            close();
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('covenantsModal.toast.errorTitle'),
              description:
                error.response.data.error ??
                t('covenantsModal.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('covenantsModal.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      });
  }, [description, status, t, addToast, close, setCovenants, covenant]);

  useEffect(() => {
    if (covenant) {
      setDescription(covenant.description);
      setStatus(covenant.status);
    }
  }, [open]);

  return (
    <AModal
      open={open}
      close={close}
      title={
        covenant ? (
          <>
            <EditOutlined /> {t('covenantsModal.titleEdit')}
          </>
        ) : (
          <>
            <FormOutlined /> {t('covenantsModal.titleCreate')}
          </>
        )
      }
      onOk={covenant ? handleEdit : handleCreate}
    >
      <Form layout="vertical" style={{ maxWidth: 600 }}>
        <Form.Item label={t('covenantsModal.fields.description')}>
          <AInput
            placeholder={t('covenantsModal.fields.descriptionPlaceholder')}
            name="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Item>
        {covenant && (
          <Form.Item label={t('covenantsModal.fields.active')}>
            <ASwitch
              checked={status}
              checkedChildren={t('common.yes')}
              unCheckedChildren={t('common.no')}
              onChange={() => {
                setStatus(!status);
              }}
            />
          </Form.Item>
        )}
      </Form>
    </AModal>
  );
};

export default CovenantCreateEdit;
